import { useId } from 'react'
import type { ShowResultsReleasePayloadOutput } from '@schemas'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

/** The same stored aggregate is shown on both public pages and in the release review. */
export function ClubStandings({ results }: { results: ShowResultsReleasePayloadOutput }) {
  const headingId = useId()
  if (!('clubStandings' in results) || results.clubStandings === null) return null
  const standings = results.clubStandings
  const maximum = Math.max(0, ...standings.map(club => club.points))
  return (
    <section aria-labelledby={headingId} className="min-w-0 space-y-3">
      <h2 id={headingId} className="text-xl font-semibold">
        Club standings
      </h2>
      <p className="text-sm text-muted-foreground">
        Points use the show’s configured placing and bonus-award rules for eligible entries and
        entrants. Each entry credits its recorded club, with the show entrant’s club as fallback.
        Eligible staff entries count.
      </p>
      {standings.length === 0 ? (
        <p className="text-sm text-muted-foreground">No clubs are represented in these results.</p>
      ) : (
        <Table tabIndex={0}>
          <caption className="sr-only">
            Club standings by points, highest first; equal points are tied.
          </caption>
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Club</TableHead>
              <TableHead scope="col">
                <span className="sr-only">Points relative to the leading club</span>
              </TableHead>
              <TableHead scope="col" className="text-right">
                Points
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {standings.map((club, index) => (
              <TableRow key={`${club.clubName}-${index}`}>
                <TableHead scope="row" className="max-w-64 whitespace-normal break-words">
                  {club.clubName}
                </TableHead>
                <TableCell className="w-1/3">
                  <div
                    aria-hidden="true"
                    className="h-2.5 rounded-r bg-primary"
                    style={{
                      width: `${maximum ? (Math.max(0, club.points) / maximum) * 100 : 0}%`,
                    }}
                  />
                </TableCell>
                <TableCell className="text-right tabular-nums">{club.points}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  )
}
